import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faSpinner, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { apiService } from '@/utils/api';
import { Site, Integration } from '@/types';

const Sync: React.FC = () => {
  const [formData, setFormData] = useState({
    site_id: '',
    master_system_id: '',
    slave_system_id: '',
    synchronization_type: '',
    since_last_updated: false,
    update: false,
    date_from: '',
    date_to: '',
    payment_reference: '',
  });

  const [showAdvanced, setShowAdvanced] = useState(false);

  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState('');
  const [syncDescription, setSyncDescription] = useState('');
  const [progressVariant, setProgressVariant] = useState('primary');
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);


  // Fetch sites
  const { data: sites = [], isLoading: sitesLoading } = useQuery('sites', apiService.getSites);

  // Fetch integrations
  const { data: integrations = [], isLoading: integrationsLoading } = useQuery('integrations', apiService.getIntegrations);

  // Create synchronization job mutation
  const syncMutation = useMutation(apiService.createSynchronizationJob, {
    onSuccess: (data: any) => {
      // Check if the response indicates failure (e.g., "Job is already running")
      if (data && data.success === false) {
        alert(data.message || 'Failed to start synchronization job');
        return;
      }
      
      // Only proceed if we have a valid job ID
      if (data && data.id) {
        setIsRunning(true);
        setSyncDescription(`${formData.synchronization_type}`);
        pollSynchronizationJob(data.id);
      } else {
        alert('Invalid response from server');
      }
    },
    onError: (error) => {
      console.error('Sync failed:', error);
      alert('Failed to start synchronization job');
    },
  });

  const synchronizationTypes = [
    { value: '', label: 'Select One' },
    // Products group
    { value: 'update_stock', label: 'Update stock', group: 'Products' },
    { value: 'product_create', label: 'Create Products', group: 'Products' },
    { value: 'price_update', label: 'Price Update', group: 'Products' },
    { value: 'reactivate_activate_products', label: 'Reactivate/Activate products', group: 'Products' },
    // Invoices/orders group
    { value: 'invoice_create', label: 'Create invoices', group: 'Invoices/orders' },
    { value: 'customer_sync', label: 'Sync customers', group: 'Invoices/orders' },
    { value: 'order_update', label: 'Update orders', group: 'Invoices/orders' },
    { value: 'order_delivery_state_listen', label: 'Order Delivery State Listener', group: 'Invoices/orders' },
    { value: 'order_create', label: 'Create orders', group: 'Invoices/orders' },
    { value: 'update_invoice_payment_status', label: 'Update Invoice payment status', group: 'Invoices/orders' },
    // Reconciliation group
    { value: 'transaction_to_invoice_payments', label: 'Match Payment transaction with invoices', group: 'Reconciliation' },
    { value: 'resync_failed_transaction_to_invoice_payments', label: 'Resync failed Payment transactions', group: 'Reconciliation' },
    { value: 'payout_to_accumulated_report_voucher', label: 'Create Payment report', group: 'Reconciliation' },
    { value: 'transaction_to_voucher', label: 'Create transaction vouchers', group: 'Reconciliation' },
    { value: 'payout_to_daily_sales_and_fees_vouchers', label: 'Book daily sales and fees', group: 'Reconciliation' },
  ];

  const pollSynchronizationJob = async (jobId: number) => {
    setStatusText('Preparing...');
    setProgress(0);
    setProgressVariant('primary');

    const poll = async () => {
      try {
        const result = await apiService.getSynchronizationJob(jobId);

        setProgress(result.progress || 0);

        if (result.details) {
          setStatusText(prev => prev + '\n' + result.details);
        }

        switch (result.status) {
          case 'done':
            setProgressVariant('success');
            break;
          case 'error':
            setProgressVariant('danger');
            break;
          default:
            setProgressVariant('primary');
        }

        if (result.progress === 100) {
          if (intervalId) {
            clearInterval(intervalId);
            setIntervalId(null);
          }
          setIsRunning(false);
        }
      } catch (error) {
        console.error('Polling error:', error);
        setProgressVariant('danger');
        setStatusText('Error occurred during synchronization');
        setIsRunning(false);
      }
    };

    // Start polling
    const id = setInterval(poll, 5000);
    setIntervalId(id);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const jobData: any = {
      site_id: parseInt(formData.site_id),
      master_system_id: parseInt(formData.master_system_id),
      slave_system_id: parseInt(formData.slave_system_id),
      synchronization_type: formData.synchronization_type,
      since_last_updated: formData.since_last_updated,
      update: formData.update,
    };

    // Only include optional fields if they have values
    if (formData.date_from) {
      jobData.date_from = formData.date_from;
    }
    if (formData.date_to) {
      jobData.date_to = formData.date_to;
    }
    if (formData.payment_reference) {
      jobData.payment_reference = formData.payment_reference;
    }

    syncMutation.mutate(jobData);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [intervalId]);

  if (sitesLoading || integrationsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="section-title">Manual Sync</h1>
      </div>

      <div className="card">
        {!isRunning ? (
          <form onSubmit={handleSubmit} className="form-section">
            <div className="form-row">
              <div className="form-column">
                <div>
                  <label className="form-label form-label-required">Site:</label>
                  <select
                    value={formData.site_id}
                    onChange={(e) => handleInputChange('site_id', e.target.value)}
                    className="form-input"
                    required
                  >
                    <option value="">Select Site</option>
                    {Array.isArray(sites) && sites.map((site: Site) => (
                      <option key={site.id} value={site.id}>
                        {site.url}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="form-label form-label-required">Master System</label>
                  <p className="text-sm text-gray-600 mb-2">System to sync to/from</p>
                  <select
                    value={formData.master_system_id}
                    onChange={(e) => handleInputChange('master_system_id', e.target.value)}
                    className="form-input"
                    required
                  >
                    <option value="">Select Master System</option>
                    {Array.isArray(integrations) && integrations.map((integration: Integration) => (
                      <option key={integration.id} value={integration.id}>
                        {integration.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="form-label form-label-required">Slave System:</label>
                  <select
                    value={formData.slave_system_id}
                    onChange={(e) => handleInputChange('slave_system_id', e.target.value)}
                    className="form-input"
                    required
                  >
                    <option value="">Select Slave System</option>
                    {Array.isArray(integrations) && integrations.map((integration: Integration) => (
                      <option key={integration.id} value={integration.id}>
                        {integration.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-column">
                <div>
                  <label className="form-label form-label-required">Integration Type:</label>
                  <select
                    value={formData.synchronization_type}
                    onChange={(e) => handleInputChange('synchronization_type', e.target.value)}
                    className="form-input"
                    required
                  >
                    {synchronizationTypes.map((type) => {
                      if (type.group) {
                        // This will be handled by the optgroup logic below
                        return null;
                      }
                      return (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      );
                    })}

                    {/* Products group */}
                    <optgroup label="Products">
                      {synchronizationTypes
                        .filter(type => type.group === 'Products')
                        .map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                    </optgroup>

                    {/* Invoices/orders group */}
                    <optgroup label="Invoices/orders">
                      {synchronizationTypes
                        .filter(type => type.group === 'Invoices/orders')
                        .map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                    </optgroup>

                    {/* Reconciliation group */}
                    <optgroup label="Reconciliation">
                      {synchronizationTypes
                        .filter(type => type.group === 'Reconciliation')
                        .map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                    </optgroup>
                  </select>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="since_last_updated"
                      checked={formData.since_last_updated}
                      onChange={(e) => handleInputChange('since_last_updated', e.target.checked)}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <label htmlFor="since_last_updated" className="ml-2 text-sm font-medium text-gray-700">
                      Since last updated
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="update"
                      checked={formData.update}
                      onChange={(e) => handleInputChange('update', e.target.checked)}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <label htmlFor="update" className="ml-2 text-sm font-medium text-gray-700">
                      Update existing
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Advanced Options Toggle */}
            <div className="flex justify-end pt-4">
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-sm text-orange-600 hover:text-orange-700 font-medium"
              >
                {showAdvanced ? 'Hide Advanced' : 'Advanced'}
              </button>
            </div>

            {/* Advanced Options */}
            {showAdvanced && (
              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Advanced Options</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="form-label">Date from:</label>
                    <input
                      type="date"
                      value={formData.date_from}
                      onChange={(e) => handleInputChange('date_from', e.target.value)}
                      className="form-input"
                    />
                  </div>

                  <div>
                    <label className="form-label">Date to:</label>
                    <input
                      type="date"
                      value={formData.date_to}
                      onChange={(e) => handleInputChange('date_to', e.target.value)}
                      className="form-input"
                    />
                  </div>

                  <div>
                    <label className="form-label">Payment reference:</label>
                    <input
                      type="text"
                      placeholder="Optional reference"
                      value={formData.payment_reference}
                      onChange={(e) => handleInputChange('payment_reference', e.target.value)}
                      className="form-input"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end pt-6">
              <button
                type="submit"
                disabled={syncMutation.isLoading}
                className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 flex items-center space-x-2"
              style={{ backgroundColor: 'hsl(15 100% 52%)' }}
              >
                {syncMutation.isLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                    <span>Starting...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPlay} className="w-4 h-4" />
                    <span>Start Sync</span>
                  </>
                )}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Synchronization Progress</h3>
              <p className="text-gray-600 mb-4">{syncDescription}</p>
            </div>

            <div className="space-y-4">
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all duration-300 ${
                    progressVariant === 'success' ? 'bg-green-500' :
                    progressVariant === 'danger' ? 'bg-red-500' :
                    'bg-orange-500'
                  }`}
                  style={{ width: `${progress}%` }}
                ></div>
              </div>

              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>{progress}% Complete</span>
                <div className="flex items-center space-x-2">
                  {progressVariant === 'success' && (
                    <>
                      <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4 text-green-500" />
                      <span className="text-green-600">Completed</span>
                    </>
                  )}
                  {progressVariant === 'danger' && (
                    <>
                      <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4 text-red-500" />
                      <span className="text-red-600">Error</span>
                    </>
                  )}
                  {progressVariant === 'primary' && (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 text-orange-500 animate-spin" />
                      <span className="text-orange-600">Running</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {statusText && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Status Log:</h4>
                <pre className="text-sm text-gray-600 whitespace-pre-wrap">{statusText}</pre>
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={() => {
                  if (intervalId) {
                    clearInterval(intervalId);
                    setIntervalId(null);
                  }
                  setIsRunning(false);
                  setProgress(0);
                  setStatusText('');
                }}
                className="btn btn-secondary"
              >
                Reset
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sync;
